import React, { PropsWithChildren } from "react";
import styled from "styled-components/macro";
import { colors } from "theme/Theme";
import ProductCardSkeleton from "components/Product/ProductCardSkeleton";

interface Props<T> {
  data: T[];
  ComponentToRender: React.FC<{ item: T }>;
  isLoading: boolean;
  skeletonCount?: number;
}

const ContentGrid = <T,>({
  data,
  ComponentToRender,
  isLoading,
  skeletonCount = 8,
}: PropsWithChildren<Props<T>>) => {
  const showSkeletons = isLoading && data.length === 0;

  return showSkeletons ? (
    <Products aria-busy="true">
      <LoadingStatus role="status">Зареждане на продукти</LoadingStatus>
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <ProductItem key={`product-skeleton-${index}`}>
          <ProductCardSkeleton />
        </ProductItem>
      ))}
    </Products>
  ) : data.length > 0 ? (
    <Products aria-busy={isLoading}>
      {isLoading && (
        <LoadingStatus role="status">Зареждане на продукти</LoadingStatus>
      )}
      {data?.map((item, idx) => {
        const itemKey =
          (item as any)?._id ?? (item as any)?.id ?? `content-item-${idx}`;

        return (
          <ProductItem key={itemKey}>
            <ComponentToRender item={item} />
          </ProductItem>
        );
      })}
    </Products>
  ) : (
    <NoResults className="text-md">
      Няма продукти отговарящи на вашето търсене
    </NoResults>
  );
};

const Products = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin: 20px 0 32px;
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, 250px), 1fr)
  );
  align-items: stretch;
  gap: clamp(16px, 2vw, 26px);
`;
Products.displayName = "Products";

const ProductItem = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
`;
ProductItem.displayName = "ProductItem";

const LoadingStatus = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const NoResults = styled.div`
  margin-top: 20px;
  padding: 40px;
  text-align: center;
  font-weight: 500;
  background-color: ${colors.lightest_blue};
  color: ${colors.darkest};
  border-radius: 12px;
`;

export default ContentGrid;
